import { useState, useRef, useEffect, useMemo } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { ChevronDown } from 'lucide-react';
import styles from './VariablePickerPanel.module.css';
import {
  VariableListItem,
  variableListItemRef,
  variableRefName,
} from '../variables/variableUtils';
import { Flex } from 'antd';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import { getVariableTypeLabel } from './variableTypeLabels';

/** When the filtered list has this many items or fewer, all groups auto-expand. */
const AUTO_EXPAND_THRESHOLD = 10;

export interface VariablePickerPanelProps {
  items: VariableListItem[];
  onSelect: (item: VariableListItem) => void;
  /** Called on Escape or Backspace-on-empty-search. Wrapper decides what to do. */
  onDismiss: () => void;
  /** Auto-focus the search input on mount (default: true) */
  autoFocus?: boolean;
}

interface ItemGroup {
  nodeId: string;
  groupName: string;
  getGroupIcon?: (size: number) => React.ReactNode;
  items: VariableListItem[];
}

/** Groups a flat list of variables by their source node, preserving insertion order. */
function groupByNode(items: VariableListItem[]): ItemGroup[] {
  const groups: ItemGroup[] = [];
  const map = new Map<string, ItemGroup>();

  for (const item of items) {
    let group = map.get(item.nodeId);
    if (!group) {
      group = {
        nodeId: item.nodeId,
        groupName: item.groupName,
        getGroupIcon: item.getGroupIcon,
        items: [],
      };
      map.set(item.nodeId, group);
      groups.push(group);
    }
    group.items.push(item);
  }

  return groups;
}

/** Renders as a clickable button with chevron when foldout is active, plain div otherwise. */
function GroupHeader({
  group,
  index,
  foldoutEnabled,
  expanded,
  onToggle,
}: {
  group: ItemGroup;
  index: number;
  foldoutEnabled: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const content = (
    <>
      <Flex className='w-full' justify='space-between' align='center'>
        <Flex align='center' gap={12}>
          <span className={styles.groupIndex}>{index + 1}.</span>
          {group.getGroupIcon && (
            <div>
              <span className={styles.groupHeaderIcon}>
                {group.getGroupIcon(16)}
              </span>
            </div>
          )}
          {group.groupName} ({group.items.length})
        </Flex>
        {foldoutEnabled && (
          <ChevronDown
            size={14}
            className={`${styles.chevron} ${expanded ? styles.chevronExpanded : ''}`}
          />
        )}
      </Flex>
    </>
  );

  if (foldoutEnabled) {
    return (
      <>
        <button
          type='button'
          className={`${styles.groupHeader} ${styles.groupHeaderClickable}`}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {content}
        </button>
      </>
    );
  }

  return <div className={styles.groupHeader}>{content}</div>;
}

/**
 * Searchable, keyboard-navigable list of workflow variables grouped by source
 * node. The basis for InlineVariablePicker and DropdownVariablePicker.
 */
export function VariablePickerPanel({
  items,
  onSelect,
  onDismiss,
  autoFocus = true,
}: VariablePickerPanelProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // Default the first group to expanded so the user sees something immediately.
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set(items.length > 0 ? [items[0].nodeId] : [])
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const filtered = useMemo(() => {
    if (!query) {
      return items;
    }
    const q = query.toLowerCase();
    return items.filter(
      (item) =>
        item.variableName.toLowerCase().includes(q) ||
        item.derivedVariableName.toLowerCase().includes(q) ||
        item.groupName.toLowerCase().includes(q) ||
        item.displayName?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const groups = useMemo(() => groupByNode(filtered), [filtered]);

  // Short lists or a single group show everything without foldout controls.
  // Longer lists with multiple groups show chevrons and respect manual expand/collapse.
  const foldoutEnabled =
    filtered.length > AUTO_EXPAND_THRESHOLD && groups.length > 1;
  const effectiveExpanded = useMemo(() => {
    if (!foldoutEnabled) {
      return new Set(groups.map((g) => g.nodeId));
    }
    return expandedNodes;
  }, [foldoutEnabled, groups, expandedNodes]);

  // Only items in expanded groups are keyboard-navigable and visible.
  const visibleItems = useMemo(() => {
    return filtered.filter((item) => effectiveExpanded.has(item.nodeId));
  }, [filtered, effectiveExpanded]);

  // Reset cursor to the top so it doesn't point past the end of a shorter list.
  useEffect(() => {
    setActiveIndex(0);
  }, [visibleItems]);

  function toggleGroup(nodeId: string) {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }

  // Keyboard nav indexes into visibleItems (not filtered), so collapsed
  // groups are skipped entirely.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (visibleItems.length > 0) {
        setActiveIndex((i) => Math.min(i + 1, visibleItems.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleItems.length > 0) {
        setActiveIndex((i) => Math.max(i - 1, 0));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = visibleItems[activeIndex];
      if (item) {
        onSelect(item);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    } else if (e.key === 'Backspace' && query === '') {
      e.preventDefault();
      onDismiss();
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.search}>
        <SearchOutlined className={styles.searchIcon} />
        <input
          ref={inputRef}
          placeholder={'Search available variables...'}
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
        />
      </div>
      <div className={styles.list}>
        {filtered.length === 0 ? (
          <div className={styles.empty}>No variables found</div>
        ) : (
          groups.map((group, groupIndex) => (
            <div
              key={group.nodeId}
              className={styles.group}
              data-testid={`group-${group.nodeId}`}
            >
              {/* {groupIndex > 0 && <hr className={styles.divider} />} */}
              <GroupHeader
                group={group}
                index={groupIndex}
                foldoutEnabled={foldoutEnabled}
                expanded={effectiveExpanded.has(group.nodeId)}
                onToggle={() => toggleGroup(group.nodeId)}
              />
              {!(
                groupIndex === groups.length - 1 &&
                !effectiveExpanded.has(group.nodeId)
              ) && <hr className={styles.divider} />}
              {/* Animated collapse wrapper — always rendered, height transitions via CSS grid */}
              <div
                className={`${styles.groupItems} ${effectiveExpanded.has(group.nodeId) ? styles.groupItemsExpanded : ''}`}
                hidden={!effectiveExpanded.has(group.nodeId) || undefined}
              >
                <div className={styles.groupItemsInner}>
                  {group.items.map((item) => {
                    const flatIndex = visibleItems.indexOf(item);

                    return (
                      <div
                        key={`${item.variableName}.${item.derivedVariableName}.${item.source}`}
                        className={styles.itemWrapper}
                      >
                        <div
                          className={`${styles.item} ${flatIndex === activeIndex ? styles.itemActive : ''}`}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onSelect(item);
                          }}
                        >
                          <EllipsisTooltip
                            text={item.displayName || item.variableName}
                            className={styles.itemLabel}
                          />
                          <div className={styles.exampleGroup}>
                            {getVariableTypeLabel(item.type) && (
                              <span className={styles.typeBadge}>
                                {getVariableTypeLabel(item.type)}
                              </span>
                            )}
                            <EllipsisTooltip
                              text={
                                item.exampleString ||
                                `{${variableRefName(variableListItemRef(item))}}`
                              }
                              className={styles.exampleText}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
